import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Products from "./Products";
import BannerSlider from "./BannerSlider";
import CategorySlider from "./CategorySlider";
import ProductSlider from "./ProductSlider";
import logo from "../assets/logo.png";
import {
  FaWallet,
  FaShareAlt,
  FaProductHunt,
  FaChartLine,
  FaGift,
  FaCar,
  FaCrown,
  FaUsers,
  FaStar,
  FaRocket,
  FaStore,
  FaTruck,
  FaAward,
  FaGem,
  FaExchangeAlt,
  FaMobile,
  FaDesktop,
  FaGlobe,
  FaTrophy,
  FaRupeeSign,
  FaPercent,
  FaCheckCircle,
  FaUserTie,
  FaBuilding,
  FaHeadset,
  FaShieldAlt,
  FaChartBar,
  FaTree,
  FaInfinity,
  FaHandHoldingUsd,
  FaCoins,
  FaUniversity,
  FaQrcode,
  FaArrowRight,
  FaPlayCircle,
  FaPhone,
  FaEnvelope,
  FaWhatsapp
} from "react-icons/fa";

// Color palette
const colors = {
  brightBlue: '#00A9EC',
  deepOrange: '#E06207',
  nearBlack: '#0E1306',
  green: '#018F2C',
  darkBrown: '#602005',
  burntOrange: '#A65007',
  lightOrange: '#EF9222',
  darkGreen: '#056619',
  reddishBrown: '#9C2E04',
  darkBlue: '#0067A1',
  skyBlue: '#0DC8F8',
  peachOrange: '#FDA653',
  navyBlue: '#023057',
  oliveBrown: '#604B0C',
  tealBlue: '#024D6E',
  pinkMagenta: '#A70756',
  goldenYellow: '#F3D71B',
  freshGreen: '#12B14A',
  lightGreen: '#55CB71',
  lightCyan: '#56E0F9'
};

const ProductHome = () => {
  const navigate = useNavigate();
  const [headerHeight, setHeaderHeight] = useState(80); // Default height

  useEffect(() => {
    // Get actual header height dynamically
    const updateHeaderHeight = () => {
      const header = document.querySelector('header');
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    // Update on mount and resize
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    // Also update on scroll (since header height changes when scrolled)
    window.addEventListener('scroll', updateHeaderHeight);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      window.removeEventListener('scroll', updateHeaderHeight);
    };
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&display=swap');

        .product-home {
          font-family: 'Inter', sans-serif;
          background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f5f3ff 100%);
          min-height: 100vh;
          position: relative;
          overflow-x: hidden;
          
        }
        @media (min-width: 640px) {
          .product-home {
            margin-top: 100px;
          }
        }
        /* Animated background particles */
        .product-home::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            radial-gradient(circle at 20% 80%, rgba(99,102,241,0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(139,92,246,0.03) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(99,102,241,0.02) 0%, transparent 50%);
          pointer-events: none;
        }

        /* Floating decoration */
        .product-home::after {
          content: '';
          position: absolute;
          top: 10%;
          right: -5%;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
          animation: float 20s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
          }
          50% {
            transform: translateY(-30px) translateX(20px);
          }
        }

        .product-home-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 20px;
          position: relative;
          z-index: 2;
        }

        /* Section spacing */
        .section-spacing {
          margin-bottom: -11px;
        }

        /* Decorative divider between sections */
        .section-divider {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          margin: 20px 0 10px;
          opacity: 0.6;
        }

        .divider-line {
          width: 60px;
          height: 2px;
          background: linear-gradient(90deg, transparent, #6366f1, #8b5cf6, #6366f1, transparent);
          border-radius: 2px;
        }

        .divider-icon {
          color: #6366f1;
          font-size: 12px;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.2);
          }
        }

        /* Scroll to top button */
        .scroll-top-btn {
          position: fixed;
          bottom: 30px;
          right: 30px;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 20px rgba(99,102,241,0.3);
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          z-index: 100;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }

        .scroll-top-btn.visible {
          opacity: 1;
          visibility: visible;
        }

        .scroll-top-btn:hover {
          transform: translateY(-5px) scale(1.1);
          box-shadow: 0 12px 28px rgba(99,102,241,0.4);
        }

        .scroll-top-btn:active {
          transform: translateY(0) scale(0.95);
        }

        /* Welcome section */
        .welcome-section {
          text-align: center;
          padding: 9px 20px 0px;
          // margin-bottom: 20px;
        }

        .welcome-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(99,102,241,0.1);
          backdrop-filter: blur(10px);
          padding: 8px 20px;
          border-radius: 100px;
          // margin-bottom: 8px;
          border: 1px solid rgba(99,102,241,0.2);
        }

        .welcome-badge span {
          font-size: 13px;
          font-weight: 500;
          color: #6366f1;
        }

        .welcome-title {
          font-size: clamp(20px, 3vw, 30px);
          font-weight: 800;
          background: linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          // margin-bottom: 16px;
          letter-spacing: -0.02em;
        }

        .welcome-subtitle {
          font-size: clamp(14px, 2vw, 16px);
          color: #64748b;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .product-home-content {
            padding: 0 16px;
          }
          .section-spacing {
            margin-bottom: 30px;
          }
          .scroll-top-btn {
            bottom: 20px;
            right: 20px;
            width: 40px;
            height: 40px;
          }
          .welcome-section {
            padding: 9px 16px 0px;
          }
          .welcome-badge {
            padding: 6px 16px;
          }
          .welcome-badge span {
            font-size: 11px;
          }
        }

        @media (max-width: 640px) {
          .product-home-content {
            padding: 0 12px;
          }
          .section-spacing {
            margin-bottom: -11px;
          }
          .divider-line {
            width: 40px;
          }
        }

        /* Loading animation for sections */
        @keyframes sectionFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .banner-section {
          animation: sectionFadeIn 0.6s ease-out forwards;
        }

        .category-section {
          animation: sectionFadeIn 0.6s ease-out 0.1s forwards;
          opacity: 0;
        }

        .product-section {
          animation: sectionFadeIn 0.6s ease-out 0.2s forwards;
          opacity: 0;
        }

        /* Custom scrollbar for the whole page */
        .product-home::-webkit-scrollbar {
          width: 8px;
        }

        .product-home::-webkit-scrollbar-track {
          background: #e2e8f0;
        }

        .product-home::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-radius: 10px;
        }

        .product-home::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
        }
      `}</style>

      <div className="product-home">
        {/* Hero Section - Accounts for fixed header */}
        <section
          className="relative overflow-hidden flex items-center justify-center text-white"
          style={{
            background: `linear-gradient(135deg, ${colors.navyBlue} 0%, ${colors.darkBlue} 100%)`,
            minHeight: "25vh",
          }}
        >
          {/* Background Glow */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-0 w-40 sm:w-64 h-40 sm:h-64 rounded-full bg-white/5 blur-3xl" />
            <div className="absolute bottom-0 right-0 w-52 sm:w-80 h-52 sm:h-80 rounded-full bg-white/5 blur-3xl" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">

              {/* Logo */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
                className="flex items-center justify-center gap-2 sm:gap-3"
              >
                <img 
                  src={logo} 
                  alt="ABM Logo" 
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-contain"
                />
                {/* ABM letter boxes commented out */}
                {/* <div
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center font-bold text-lg sm:text-2xl shadow-lg"
                  style={{
                    backgroundColor: colors.pinkMagenta,
                    color: colors.navyBlue,
                  }}
                >
                  A
                </div>

                <div
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center font-bold text-lg sm:text-2xl shadow-lg"
                  style={{
                    backgroundColor: colors.freshGreen,
                    color: colors.navyBlue,
                  }}
                >
                  B
                </div>

                <div
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center font-bold text-lg sm:text-2xl shadow-lg"
                  style={{
                    backgroundColor: colors.goldenYellow,
                    color: colors.navyBlue,
                  }}
                >
                  M
                </div> */}
              </motion.div>

              {/* Company Name */}
              <motion.h1
                initial={{ opacity: 0, y: 25 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-xl sm:text-2xl md:text-4xl font-bold tracking-wide"
              >
                ABM GROUP ONE
              </motion.h1>

              {/* Slogans */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="max-w-3xl mx-auto flex flex-col"
              >
                {/* Hindi */}
                <p
                  className="text-base sm:text-lg md:text-2xl font-semibold leading-relaxed px-2"
                  style={{
                    color: colors.goldenYellow,
                    textShadow: "0 2px 8px rgba(0,0,0,0.25)",
                  }}
                >
                  “हर रोज़ का प्रोडक्ट, हर दिन की कमाई —
                  <br />
                  आज आपकी तरक्की, कल परिवार की सुरक्षा।”
                </p>

                {/* English */}
                <p
                  className="text-sm sm:text-base md:text-lg text-white/90 leading-relaxed px-2"
                  style={{
                    textShadow: "0 2px 6px rgba(0,0,0,0.15)",
                  }}
                >
                  Everyday products, daily income — your progress today,
                  your family's security tomorrow.
                </p>
              </motion.div>

            </div>
          </div>
        </section>

        <div className="product-home-content">
          {/* Welcome Section */}
          <div className="welcome-section">
            <div className="welcome-badge">
              <span>✨ Welcome to Our Store ✨</span>
            </div>
            <h1 className="welcome-title">
              Discover Amazing Deals
            </h1>
          </div>

          {/* Banner Section */}
          <div className="banner-section section-spacing">
            <BannerSlider />
          </div>

          {/* Decorative Divider */}
          {/* <div className="section-divider">
            <div className="divider-line"></div>
            <span className="divider-icon">✦</span>
            <div className="divider-line"></div>
          </div> */}

          {/* Category Section */}
          <div className="category-section section-spacing">
            <CategorySlider onCategoryClick={(category) => {
              navigate(`/dashboard?page=my-shop&category=${category._id || category.id}`);
            }} />
          </div>

          {/* Decorative Divider */}
          {/* <div className="section-divider">
            <div className="divider-line"></div>
            <span className="divider-icon">✦</span>
            <div className="divider-line"></div>
          </div> */}

          {/* Product Section */}
          <div className="product-section section-spacing">
            <ProductSlider />
          </div>

          {/* Footer Note */}
          {/* <div className="text-center py-8 mt-4 border-t border-gray-200/50">
            <p className="text-xs text-gray-400">
              © 2024 Your Store. All rights reserved. |
              <span className="mx-1">❤️</span>
              Made with love
            </p>
          </div> */}
        </div>

        {/* Scroll to Top Button */}
        <button
          className="scroll-top-btn"
          id="scrollTopBtn"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Scroll to top"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
      </div>

      <script dangerouslySetInnerHTML={{
        __html: `
          // Scroll to top button visibility
          const scrollBtn = document.getElementById('scrollTopBtn');
          if (scrollBtn) {
            window.addEventListener('scroll', () => {
              if (window.scrollY > 300) {
                scrollBtn.classList.add('visible');
              } else {
                scrollBtn.classList.remove('visible');
              }
            });
          }
        `
      }} />
    </>
  );
};

export default ProductHome;